"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import { motion, useInView } from "framer-motion";
import AnimatedSection from "./AnimatedSection";

/**
 * Editorial magazine-style "by the numbers" section.
 * Full-bleed dark canvas with oversized typography, asymmetric grid,
 * and animated number tickers that count up on view-in.
 */
export default function ByTheNumbersSection() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-redy-charcoal text-white overflow-hidden">
      {/* Background atmosphere — orange glow top-left, red bottom-right */}
      <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-redy-orange/20 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] bg-redy-red/15 rounded-full blur-[160px] pointer-events-none" />

      {/* Editorial grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        {/* Eyebrow + headline */}
        <AnimatedSection className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
                {t("Redy, by the numbers", "Redy, en números")}
              </div>
              <h2 className="text-5xl md:text-[80px] font-black tracking-tight leading-[0.92]">
                {t("The math", "Las cuentas")}
                <br />
                <span className="redy-text-gradient">
                  {t("isn't subtle.", "no son sutiles.")}
                </span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <p className="text-[15px] font-medium text-white/55 leading-relaxed">
                {t(
                  "Many POS systems charge a monthly software fee and still take a cut of every card swipe. Here's what changes with Redy.",
                  "Muchos POS cobran una mensualidad de software y aun así se quedan con una parte de cada swipe. Esto es lo que cambia con Redy."
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* Asymmetric numbers grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-12 md:gap-y-20">
          {/* $0 — biggest, top-left, 5 cols */}
          <NumberCell
            className="col-span-2 md:col-span-5"
            value="$0"
            valueClass="text-[120px] md:text-[200px] redy-text-gradient"
            labelKicker={t("Start plan software", "Software del plan Start")}
            labelBody={t("When you process payments with Redy. Move to Plus or Pro when you need more.", "Si procesas tus pagos con Redy. Pasa a Plus o Pro cuando necesites más.")}
          />

          {/* 4% — top-right, 5 cols */}
          <NumberCell
            className="col-span-2 md:col-span-5 md:col-start-8"
            value="4%"
            valueClass="text-[120px] md:text-[200px] text-white"
            labelKicker={t("Default cash discount", "Descuento en efectivo por defecto")}
            labelBody={t("Dual pricing: the menu shows the card price, designed to cover the processing cost. Cash payers get the discount.", "Dual pricing: el menú muestra el precio con tarjeta, pensado para cubrir el costo de procesamiento. Quien paga en efectivo recibe el descuento.")}
          />

          {/* Divider line */}
          <div className="col-span-full h-px bg-white/8" />

          {/* 19 features — 4 cols */}
          <NumberCellCounter
            className="col-span-1 md:col-span-4"
            target={19}
            valueClass="text-[80px] md:text-[120px] text-white"
            labelKicker={t("Features across both verticals", "Features entre las dos verticales")}
            labelBody={t("POS, KDS, kiosk, website, online ordering, time clock, inventory, loyalty, SMS — and more. Some depend on your plan.", "POS, KDS, kiosko, sitio, pedidos, reloj checador, inventario, lealtad, SMS — y más. Algunos dependen de tu plan.")}
          />

          {/* 2 verticals — 4 cols, center */}
          <NumberCellCounter
            className="col-span-1 md:col-span-4"
            target={2}
            valueClass="text-[80px] md:text-[120px] redy-text-gradient"
            labelKicker={t("Verticals", "Verticales")}
            labelBody={t("Restaurant + Nightlife. Same platform, separate playbooks. Pick the one that fits your business.", "Restaurant + Nightlife. Misma plataforma, playbooks separados. Elige el que se acomoda a tu negocio.")}
          />

          {/* ∞ devices — 4 cols */}
          <NumberCell
            className="col-span-2 md:col-span-4"
            value="∞"
            valueClass="text-[80px] md:text-[120px] text-white"
            labelKicker={t("Devices and users", "Dispositivos y usuarios")}
            labelBody={t("On Pro: unlimited POS, KDS, kiosks and employees.", "Con Pro: POS, KDS, kioskos y empleados ilimitados.")}
          />

          {/* Divider line */}
          <div className="col-span-full h-px bg-white/8" />

          {/* Bottom row: 3 small editorial stats */}
          <SmallStat
            className="col-span-1 md:col-span-4"
            value="Live"
            label={t("Menu boards update from the POS in seconds", "Los menu boards se actualizan desde el POS en segundos")}
          />
          <SmallStat
            className="col-span-1 md:col-span-4"
            value="$249"
            label={t("One-time PAX terminal, set up with Redy payments — yours to keep", "Terminal PAX de pago único, configurado con pagos de Redy — tuyo")}
          />
          <SmallStat
            className="col-span-2 md:col-span-4"
            value={t("Monthly", "Mensual")}
            label={t("Month to month, no long-term contract.", "Mes a mes, sin contrato a largo plazo.")}
          />
        </div>

        {/* Bottom callout */}
        <AnimatedSection delay={0.3} className="mt-24">
          <div className="bg-white/[0.04] border border-white/10 rounded-[24px] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-2">
                {t("Example: $20K/month in card sales at a 2.6% rate", "Ejemplo: $20K/mes en tarjeta a una tasa de 2.6%")}
              </div>
              <div className="text-[28px] md:text-[36px] font-black text-white leading-tight">
                {t("Card payers cover", "La tarjeta cubre")}{" "}
                <span className="redy-text-gradient">~$520/mo</span>
                <span className="text-white/55"> {t("in fees", "en fees")}</span>
              </div>
            </div>
            <Link
              href="/pricing"
              className="inline-flex items-center gap-2 text-[14px] font-bold text-white bg-redy-orange px-7 py-3.5 rounded-full hover:bg-redy-red transition-all active:scale-95 shadow-lg whitespace-nowrap"
            >
              {t("Estimate my savings →", "Estima mi ahorro →")}
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NumberCell — static oversized number with label
// ─────────────────────────────────────────────────────────────────────────────

function NumberCell({
  className = "",
  value,
  valueClass,
  labelKicker,
  labelBody,
}: {
  className?: string;
  value: string;
  valueClass: string;
  labelKicker: string;
  labelBody: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={className}
    >
      <div className={`${valueClass} font-black leading-[0.9] tracking-tight mb-4`}>
        {value}
      </div>
      <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-2">
        {labelKicker}
      </div>
      <p className="text-[14px] font-medium text-white/60 leading-relaxed max-w-[280px]">
        {labelBody}
      </p>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NumberCellCounter — oversized number that counts up on view-in
// ─────────────────────────────────────────────────────────────────────────────

function NumberCellCounter({
  className = "",
  target,
  valueClass,
  labelKicker,
  labelBody,
}: {
  className?: string;
  target: number;
  valueClass: string;
  labelKicker: string;
  labelBody: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 900; // ms
    const start = performance.now();
    let frame: number;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, target]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={className}
    >
      <div className={`${valueClass} font-black leading-[0.9] tracking-tight mb-4 tabular-nums`}>
        {count}
      </div>
      <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-2">
        {labelKicker}
      </div>
      <p className="text-[14px] font-medium text-white/60 leading-relaxed max-w-[280px]">
        {labelBody}
      </p>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SmallStat — bottom-row supporting numbers
// ─────────────────────────────────────────────────────────────────────────────

function SmallStat({
  className = "",
  value,
  label,
}: {
  className?: string;
  value: string;
  label: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className={className}
    >
      <div className="text-[32px] md:text-[44px] font-black text-white leading-none mb-2 tracking-tight">
        {value}
      </div>
      <p className="text-[13px] font-medium text-white/55 leading-relaxed">
        {label}
      </p>
    </motion.div>
  );
}
